// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/channel/components/pickers/gif/FavoriteGifFirstTimePromptModal.module.css';
import FavoriteGif from '@app/features/expressions/state/FavoriteGif';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useRef} from 'react';

interface FavoriteGifFirstTimePromptModalProps {
	onConfirm: () => void;
}
export const FavoriteGifFirstTimePromptModal = observer(function FavoriteGifFirstTimePromptModal({
	onConfirm,
}: FavoriteGifFirstTimePromptModalProps) {
	const {t} = useLingui();
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);
	const handleConfirm = () => {
		FavoriteGif.setSaveGifFavoritesAsSavedMedia(false);
		FavoriteGif.markFirstTimePromptSeen();
		ModalCommands.pop();
		onConfirm();
	};
	const handleUseSavedMedia = () => {
		FavoriteGif.setSaveGifFavoritesAsSavedMedia(true);
		FavoriteGif.markFirstTimePromptSeen();
		ModalCommands.pop();
		onConfirm();
	};
	const handleCancel = () => {
		ModalCommands.pop();
	};
	return (
		<Modal.Root size="small" centered initialFocusRef={initialFocusRef}>
			<Modal.Header title={t`How should we save your GIF favorites?`} onClose={handleCancel} />
			<Modal.Content>
				<div className={styles.body}>
					<p className={styles.description}>
						<Trans>
							You can store starred GIFs as URL-only favorites or upload them to your Saved Media. Pick the one that
							fits how you use them — you can change it any time in Settings &gt; Chat &gt; Media.
						</Trans>
					</p>
					<ul className={styles.list}>
						<li className={styles.listItem}>
							<Trans>
								<strong>URL-only favorites (default)</strong> — synced across your devices, no upload, doesn't count
								against Saved Media. The original media may disappear if its host removes it.
							</Trans>
						</li>
						<li className={styles.listItem}>
							<Trans>
								<strong>Saved Media</strong> — uploaded, taggable, searchable, and persistent, but counts against your
								Saved Media limit.
							</Trans>
						</li>
					</ul>
					<p className={styles.hint}>
						<Trans>We'll only ask once.</Trans>
					</p>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={handleUseSavedMedia}>
					<Trans>Use Saved Media</Trans>
				</Button>
				<Button variant="primary" onClick={handleConfirm} ref={initialFocusRef}>
					<Trans>Use URL-only (recommended)</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
